import { urlIdentityService } from '@src/endpoints'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import toast from 'react-hot-toast'
import { ToastContent } from "@src/views/components/toast/ToastContent"

const ToastMessage = (heading, message, isSuccess) => {
  toast(t => (
    <ToastContent t={t} heading={heading} response={isSuccess} message={message} />
  ))
}

// Helper: Fetch all External Dispatch users with full details
const fetchExternalDispatchUsers = async () => {
  const selectResponse = await axios.get(`${urlIdentityService}/GetAllUserSelect/${encodeURIComponent('External Dispatch')}`)
  const selectData = selectResponse.data?.data || selectResponse.data || []

  if (!Array.isArray(selectData) || selectData.length === 0) return []

  // Fetch full details for each user in parallel; fall back to partial select data on failure
  const detailPromises = selectData.map(u => axios.get(`${urlIdentityService}/GetUserDetailsById/${u.value}`)
    .then(res => res.data)
    .catch(() => ({
      // Fallback: map GetAllUserSelect fields to the shape columns expect
      // Note: GetAllUserSelect doesn't return emailConfirmed/lockedOut,
      // so we use null (unknown) rather than false (misleadingly unchecked)
      id: u.value,
      userId: u.value,
      fullName: u.fullName || u.label || '',
      firstName: (u.fullName || '').split(' ')[0] || '',
      lastName: (u.fullName || '').split(' ').slice(1).join(' ') || '',
      email: '',
      phoneNumber: '',
      companyId: u.companyId,
      companyCode: u.companyCode,
      companyName: u.companyName || '',
      userStatus: 'active',
      emailConfirmed: null,
      lockedOut: null,
      userCreatedDate: null,
      _partial: true
    }))
  )
  const details = await Promise.all(detailPromises)
  return details.filter(Boolean)
}

// Client-side filter/sort/paginate helper
const filterSortPaginate = (allUsers, params) => {
  let filtered = [...allUsers]

  if (params.status) {
    filtered = filtered.filter(u => u.userStatus?.toLowerCase() === params.status.toLowerCase())
  }

  if (params.confirmed) {
    filtered = filtered.filter(u => u.emailConfirmed === true)
  }

  if (params.companyName) {
    filtered = filtered.filter(u => u.companyName?.toLowerCase().includes(params.companyName.toLowerCase()))
  }

  if (params.q) {
    const q = params.q.toLowerCase()
    filtered = filtered.filter(u => u.fullName?.toLowerCase().includes(q) || u.firstName?.toLowerCase().includes(q) || u.lastName?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q))
  }

  // Sort
  const sortField = params.sortColumn || 'id'
  const sortDir = params.sort === 'asc' ? 1 : -1
  filtered.sort((a, b) => {
    const aVal = (a[sortField] || '').toString().toLowerCase()
    const bVal = (b[sortField] || '').toString().toLowerCase()
    return aVal.localeCompare(bVal) * sortDir
  })

  const total = filtered.length

  // Paginate
  const page = params.page || 1
  const perPage = params.perPage || 10
  const start = (page - 1) * perPage
  const paged = filtered.slice(start, start + perPage)

  return { data: paged, total }
}

export const getAllData = createAsyncThunk('dispatchAccess/getAllData', async () => {
  const allUsers = await fetchExternalDispatchUsers()

  const allCustomersCount = allUsers.length
  const activeUsersCount = allUsers.filter(u => u.userStatus?.toLowerCase() === 'active').length
  const inactiveUsersCount = allUsers.filter(u => u.userStatus?.toLowerCase() === 'inactive').length
  const allEmailConfirmedCount = allUsers.filter(u => u.emailConfirmed === true).length

  return {
    allUsers,
    allCustomersCount,
    activeUsersCount,
    inactiveUsersCount,
    allEmailConfirmedCount
  }
})

export const getData = createAsyncThunk('dispatchAccess/getData', async (params, { getState }) => {
  let allUsers = getState().dispatchAccess.allUsers

  // If cache is empty, fetch fresh
  if (!allUsers || allUsers.length === 0) {
    allUsers = await fetchExternalDispatchUsers()
  }

  const { data, total } = filterSortPaginate(allUsers, params)

  return {
    params,
    data,
    allUsers,
    totalPages: total,
    loaded: true
  }
})

export const getUser = createAsyncThunk('dispatchAccess/getUser', async id => {
  const response = await axios.get(`${urlIdentityService}/GetUserDetailsById/${id}`)
  return {
    data: response.data,
    userLoaded: true
  }
})

export const addUser = createAsyncThunk('dispatchAccess/addUser', async (user, { dispatch, getState }) => {
  const response = await axios.post(`${urlIdentityService}/Register`, user)
  if (response.data.success === true) {
    // Auto-confirm email so the user can log in (RequireConfirmedAccount is enabled)
    const newUserId = response.data.userId
    if (newUserId) {
      try {
        await axios.put(`${urlIdentityService}/UpdateUserAccess`, { id: newUserId, isEmail: true, value: true })
      } catch (err) {
        console.warn('Failed to auto-confirm email for new dispatch user:', err)
      }
    }
    ToastMessage('Dispatch User', 'User created successfully', true)
    // Re-fetch fresh data (clears cache by fetching from API)
    await dispatch(getAllData())
    await dispatch(getData(getState().dispatchAccess.params))
    return { success: true, user }
  } else {
    const errorMsg = response.data.messages || response.data.errors?.[0] || 'Failed to create user'
    ToastMessage('Dispatch User', errorMsg, false)
    return { success: false, error: errorMsg }
  }
})

export const updateUser = createAsyncThunk('dispatchAccess/updateUser', async (userProfileUpdateViewModel, { dispatch }) => {
  const response = await axios.put(`${urlIdentityService}/UserProfileUpdate`, userProfileUpdateViewModel)
  response.data.success === true ? ToastMessage('Profile', response.data.messages[0], true) : ToastMessage('Profile', response.data.errors[0], false)
  await dispatch(getUser(userProfileUpdateViewModel.id))
  return userProfileUpdateViewModel
})

export const updateUserAccess = createAsyncThunk('dispatchAccess/updateUserAccess', async (updateUserAccessViewModel, { }) => {
  const response = await axios.put(`${urlIdentityService}/UpdateUserAccess`, updateUserAccessViewModel)
  // Return both the API result and the original payload so the reducer can patch the cache
  return { response: response.data, ...updateUserAccessViewModel }
})

export const suspendUser = createAsyncThunk('dispatchAccess/suspendUser', async (userId, { dispatch }) => {
  try {
    await axios.delete(`${urlIdentityService}/SuspendUser/${userId}`)
    ToastMessage('Dispatch User', 'User suspended successfully', true)
  } catch (error) {
    const msg = error.response?.status === 403 ? 'Insufficient permissions — contact HR to suspend users' : (error.response?.data?.messages?.[0] || 'Failed to suspend user')
    ToastMessage('Dispatch User', msg, false)
  }
  dispatch(getUser(userId))
})

export const reinstateUser = createAsyncThunk('dispatchAccess/reinstateUser', async (userId, { dispatch }) => {
  try {
    await axios.patch(`${urlIdentityService}/ReinstateUser/${userId}`)
    ToastMessage('Dispatch User', 'User reinstated successfully', true)
  } catch (error) {
    const msg = error.response?.status === 403 ? 'Insufficient permissions — contact HR to reinstate users' : (error.response?.data?.messages?.[0] || 'Failed to reinstate user')
    ToastMessage('Dispatch User', msg, false)
  }
  dispatch(getUser(userId))
})

export const dispatchAccessSlice = createSlice({
  name: 'dispatchAccess',
  initialState: {
    loaded: false,
    userLoaded: false,
    data: [],
    total: 1,
    params: {},
    allData: [],
    allUsers: [],
    selectedUser: null
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getAllData.fulfilled, (state, action) => {
        state.allUsers = action.payload.allUsers
        state.allData = {
          allCustomersCount: action.payload.allCustomersCount,
          activeUsersCount: action.payload.activeUsersCount,
          inactiveUsersCount: action.payload.inactiveUsersCount,
          allEmailConfirmedCount: action.payload.allEmailConfirmedCount
        }
      })
      .addCase(getData.pending, (state) => {
        state.loaded = false
      })
      .addCase(getData.fulfilled, (state, action) => {
        state.loaded = action.payload.loaded
        state.data = action.payload.data
        state.params = action.payload.params
        state.total = action.payload.totalPages
        if (action.payload.allUsers) {
          state.allUsers = action.payload.allUsers
        }
      })
      .addCase(getUser.pending, (state) => {
        state.userLoaded = false
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.userLoaded = true
        state.selectedUser = action.payload.data
      })
      // After UpdateUserAccess PUT succeeds, patch the cached user in allUsers + data
      // because GetUserDetailsById doesn't return emailConfirmed/lockedOut fields.
      .addCase(updateUserAccess.fulfilled, (state, action) => {
        const { id, isEmail, value, response } = action.payload
        if (response?.success) {
          const field = isEmail ? 'emailConfirmed' : 'lockedOut'
          const patchUser = (user) => {
            if (user.userId === id) {
              return { ...user, [field]: value }
            }
            return user
          }
          state.allUsers = state.allUsers.map(patchUser)
          state.data = state.data.map(patchUser)
        }
      })
  }
})

export default dispatchAccessSlice.reducer